import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalSearch } from '@/hooks/system/useGlobalSearch.ts';
import SearchForm from './SearchForm';
import PatientTable from './PatientTable';
import PaginationComponent from '@/components/common/PaginationComponent';
import { Patient, GlobalSearchProps } from './types';
import { searchOptions } from './constants';

const GlobalSearch = ({ onPatientSelect }: GlobalSearchProps) => {
  const navigate = useNavigate();
  
  // Use the custom hook for all search functionality
  const {
    patients,
    isLoading,
    searchBy,
    searchTerm,
    currentPage,
    rowsPerPage,
    totalCount,
    setSearchBy,
    setSearchTerm,
    setCurrentPage,
    setRowsPerPage,
    handleSearch,
    handleReload
  } = useGlobalSearch();

  const handleAutoSearch = () => {
    // Auto-search disabled - use search button instead
  };

  // For all users, use server-side pagination (totalCount from API)
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const startIndex = (currentPage - 1) * rowsPerPage;
  
  // Apply client-side pagination when we have search results (totalCount === patients.length means client-side data)
  // Server-side pagination already returns paginated data, but search returns all results
  const paginatedPatients = patients.slice(startIndex, startIndex + rowsPerPage);

  const handlePatientClick = (patient: Patient) => {
    if (onPatientSelect && patient.pep_patient_id) {
      onPatientSelect(patient.pep_patient_id, patient.clinic_id);
    } else if (patient.pep_patient_id) {
      // Navigate to VPE page with proper back navigation params
      navigate(`/es-dashboard?from=global-search&patientId=${patient.pep_patient_id}`, {
        state: { 
          patientId: patient.pep_patient_id,
          fromGlobalSearch: true 
        }
      });
    }
  };

  return (
    <div className="min-h-screen" id="global_search_page_container">
      {/* Main Content */}
      <main id="global_search_main">
        <div id="global_search_header" className="bg-white border border-gray-200 rounded-lg">
          {/* Header with Back Button */}
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <h1 id="global_search_title" className="text-2xl font-semibold text-gray-900">Search Patients</h1>
            </div>
            <SearchForm
              searchBy={searchBy}
              setSearchBy={setSearchBy}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              onSearch={handleSearch}
              onReload={handleReload}
              isLoading={isLoading}
              searchOptions={searchOptions}
              onAutoSearch={handleAutoSearch}
            />
          </div>
        </div>

        {/* Results Section with spacing */}
        <div id="global_search_results" className="mt-6">
          <PatientTable
            patients={patients as Patient[]}
            paginatedPatients={paginatedPatients as Patient[]}
            startIndex={startIndex}
            rowsPerPage={rowsPerPage}
            onPatientClick={handlePatientClick}
            isLoading={isLoading}
          />

          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            recordsPerPage={rowsPerPage}
            totalRecords={totalCount}
            startIndex={startIndex + 1}
            endIndex={Math.min(startIndex + rowsPerPage, totalCount)}
            onPageChange={setCurrentPage}
            onRecordsPerPageChange={setRowsPerPage}
            className="px-0 py-4 bg-white"
          />
        </div>
      </main>
    </div>
  );
};

export default GlobalSearch;